/******************************************************************************
 *
 * FILE:
 * src/modules/wms/workspace/execution/components/ChecklistItem.jsx
 *
 * MODULE:
 * Work Management System (WMS)
 *
 * FEATURE:
 * Execution Workspace
 *
 * DESCRIPTION:
 * Renders a single checklist item for execution.
 * This component is responsible only for presentation
 * and user interaction.
 *
 ******************************************************************************/

import React, {
    useEffect,
    useState,
} from "react";

import ResponseControl from "./ResponseControl";

export default function ChecklistItem({

    item,

    disabled = false,

    onResponseChange,

    onRemarksChange,

    onPhotoClick,

    onAttachmentClick,

    onObservationClick,

}) {

    // ==========================================================
    // LOCAL STATE
    // ==========================================================

    const [remarks, setRemarks] = useState(
        item.remarks || ""
    );

    useEffect(() => {

        setRemarks(
            item.remarks || ""
        );

    }, [item.remarks]);

    // ==========================================================
    // RENDER
    // ==========================================================

    return (

        <div className="card shadow-sm border-0 mb-3">

            {/* ========================================================== */}
            {/* HEADER */}
            {/* ========================================================== */}

            <div className="card-header bg-white">

                <div className="d-flex justify-content-between align-items-start">

                    <div>

                        <div className="fw-bold">

                            {item.sequence}. {item.title}

                        </div>

                        {

                            item.description && (

                                <small className="text-muted">

                                    {item.description}

                                </small>

                            )

                        }

                    </div>

                    <div className="text-end">

                        {

                            item.mandatory && (

                                <span className="badge bg-danger me-2">

                                    Mandatory

                                </span>

                            )

                        }

                        {

                            item.completed

                                ?

                                <span className="badge bg-success">

                                    Completed

                                </span>

                                :

                                <span className="badge bg-warning text-dark">

                                    Pending

                                </span>

                        }

                    </div>

                </div>

            </div>

            {/* ========================================================== */}
            {/* BODY */}
            {/* ========================================================== */}

            <div className="card-body">

                {/* ====================================================== */}
                {/* RESPONSE */}
                {/* ====================================================== */}

                <div className="mb-4">

                    <label className="form-label fw-semibold">

                        Response

                    </label>

                    <ResponseControl

                        item={item}

                        value={item.response}

                        disabled={disabled}

                        onChange={(value) =>

                            onResponseChange?.(

                                item,

                                value

                            )

                        }

                    />

                </div>

                {/* ====================================================== */}
                {/* REMARKS */}
                {/* ====================================================== */}

                <div className="mb-4">

                    <label className="form-label fw-semibold">

                        Remarks

                    </label>

                    <textarea

                        className="form-control"

                        rows={3}

                        placeholder="Enter remarks..."

                        disabled={disabled}

                        value={remarks}

                        onChange={(e) =>

                            setRemarks(
                                e.target.value
                            )

                        }

                        onBlur={() =>

                            onRemarksChange?.(

                                item,

                                remarks

                            )

                        }

                    />

                </div>

                {/* ====================================================== */}
                {/* EVIDENCE */}
                {/* ====================================================== */}

                <div className="d-flex flex-wrap gap-2">

                    <button

                        type="button"

                        className="btn btn-outline-primary btn-sm"

                        onClick={() =>

                            onPhotoClick?.(item)

                        }

                    >

                        <i className="fas fa-camera me-2" />

                        Photos

                        {

                            item.photo_count > 0 && (

                                <span className="badge bg-primary ms-2">

                                    {item.photo_count}

                                </span>

                            )

                        }

                    </button>

                    <button

                        type="button"

                        className="btn btn-outline-secondary btn-sm"

                        onClick={() =>

                            onAttachmentClick?.(item)

                        }

                    >

                        <i className="fas fa-paperclip me-2" />

                        Attachments

                        {

                            item.attachment_count > 0 && (

                                <span className="badge bg-secondary ms-2">

                                    {item.attachment_count}

                                </span>

                            )

                        }

                    </button>

                    <button

                        type="button"

                        className="btn btn-outline-warning btn-sm"

                        onClick={() =>

                            onObservationClick?.(item)

                        }

                    >

                        <i className="fas fa-exclamation-circle me-2" />

                        Observations

                        {

                            item.observation_count > 0 && (

                                <span className="badge bg-warning text-dark ms-2">

                                    {item.observation_count}

                                </span>

                            )

                        }

                    </button>

                </div>

            </div>

            {/* ========================================================== */}
            {/* FOOTER */}
            {/* ========================================================== */}

            <div className="card-footer bg-light">

                <div className="d-flex justify-content-between">

                    <small className="text-muted">

                        Updated By : {item.updated_by || "-"}

                    </small>

                    <small className="text-muted">

                        {item.updated_at || "-"}

                    </small>

                </div>

            </div>

        </div>

    );

}